export interface LegacyMe {
  identityStore?: "LEGACY_SQLITE";
  accountId: string;
  subjectId: string;
  tenantId: string;
  organizationId: string;
  email: string;
  displayName: string;
  roles: string[];
  csrfToken: string;
  expiresAt: string;
  assuranceLevel: string;
  capabilities: {
    createEvidence: boolean;
    viewEvidence: boolean;
    manageOrganization: boolean;
    viewAudit: boolean;
  };
  featureFlags: {
    l1DigestSubmission: boolean;
    l2OriginalUpload: boolean;
    l2ScannerAssurance: "LOCAL_POLICY_ONLY" | "UNAVAILABLE";
    chainSubmission: boolean;
    publicVerification: boolean;
  };
}

export interface PersonalIdentity {
  id: string;
  reviewCaseId: string | null;
  status: "SUBMITTED" | "UNDER_REVIEW" | "VERIFIED" | "REJECTED" | "REVOKED";
  maskedRealName: string;
  documentType: "PRC_RESIDENT_ID" | "OTHER";
  maskedDocumentNumber: string;
  verificationMethod: "PROVIDER" | "MANUAL_REVIEW";
  verificationProvider: string | null;
  submittedAt: string;
  verifiedAt: string | null;
  rejectionRecorded: boolean;
}

export interface OrganizationBinding {
  organizationId: string;
  legalName: string;
  organizationStatus: string;
  assuranceLevel: string;
  tenantId: string | null;
  membershipStatus: string | null;
  roles: string[];
  application: null | {
    id: string;
    reviewCaseId: string | null;
    applicationNo: string;
    status: string;
    applicantAuthority: string;
    submittedAt: string;
  };
}

export interface MysqlTrustedBusinessContext {
  scopeType: "PERSONAL" | "ORGANIZATION";
  tenantId: string;
  personalIdentityId: string | null;
  organizationId: string | null;
  actorAccountId: string;
  subjectId: string;
  displayName: string;
  roles: string[];
  assuranceLevel: string;
  capabilities: {
    createEvidence: boolean;
    viewEvidence: boolean;
    viewAudit: boolean;
    manageOrganization: boolean;
  };
  featureFlags: {
    l1DigestSubmission: true;
    l2OriginalUpload: boolean;
    l2ScannerAssurance: "LOCAL_POLICY_ONLY" | "UNAVAILABLE";
    chainSubmission: false;
    publicVerification: false;
  };
}

export interface MysqlMe {
  identityStore: "MYSQL";
  accountId: string;
  maskedEmail: string;
  session: {
    id: string;
    scopeType: "ONBOARDING" | "PERSONAL" | "ORGANIZATION";
    tenantId: string | null;
    personalIdentityId: string | null;
    organizationId: string | null;
    expiresAt: string;
    idleExpiresAt: string;
    mfaVerified: true;
  };
  mfa: { totp: "ACTIVE" };
  platformRoles: Array<"PLATFORM_REVIEWER" | "PLATFORM_REVIEW_ADMIN" | "PLATFORM_SECURITY_ADMIN" | "PLATFORM_AUDITOR">;
  trustedContext: MysqlTrustedBusinessContext | null;
  personalIdentity: PersonalIdentity | null;
  organizations: OrganizationBinding[];
  onboarding: {
    state: "IDENTITY_REQUIRED" | "UNDER_REVIEW" | "READY";
    trustedBusinessAccess: boolean;
    message: string;
  };
  capabilities: {
    submitPersonalIdentity: boolean;
    submitOrganization: boolean;
    switchScope: boolean;
    listSessions: true;
    viewOwnSecurityAudit: true;
    reviewIdentities: boolean;
    viewPlatformSecurity: boolean;
    managePlatformSecurity: boolean;
  };
}

export type Me = LegacyMe | MysqlMe;

export function isMysqlMe(me: Me): me is MysqlMe {
  return me.identityStore === "MYSQL";
}
